import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import "../module_mechanics/Styles/InvUI.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart, faUser, faPhone, faMapMarkerAlt, faCalendarAlt,
    faFileInvoice, faBox, faRupeeSign, faTag, faBalanceScale, faDollarSign,
    faCalculator, faExclamationTriangle, faPlus, faMinus, faTrash, faSave,
    faTimes, faSpinner, faCheckCircle, faExclamationCircle, faSearch,
    faUpload, faReceipt, faChevronDown, faChevronUp, faBoxes, faBarcode,
    faFileAlt, faLayerGroup, faCubes, faBoxOpen, faPallet, faInfoCircle,
    faToggleOn, faToggleOff, faCopy, faList, faStore, faEnvelope, faBuilding,
    faAddressCard, faIdCard, faCreditCard, faMoneyBill, faUniversity, faWallet,
    faPercent, faDownload, faEdit, faChartLine, faTimesCircle, faShoppingBag,
    faHistory, faUndo, faRedo, faPrint, faFilePdf, faTrashAlt, faPills,
    faSyringe, faPrescription, faClipboardList
} from '@fortawesome/free-solid-svg-icons';

// ==================== API CONFIGURATION ====================
const API_BASE = 'https://ourzestapp.online/oz_backend/pharmacy_module';
const PRODUCT_API = `${API_BASE}/pharmacy_products.php`;
const PURCHASE_API = `${API_BASE}/pharmacy_purchases.php`;
const SUPPLIER_API = `${API_BASE}/pharmacy_suppliers.php`;

// ==================== PACKING TYPES CONFIGURATION ====================
const packingTypesList = [
    { value: '', label: 'Select Packing Type' },
    { value: 'box', label: 'Box', icon: faBox },
    { value: 'carton', label: 'Carton', icon: faBoxes },
    { value: 'crate', label: 'Crate', icon: faCubes },
    { value: 'packet', label: 'Packet', icon: faBoxOpen },
    { value: 'bundle', label: 'Bundle', icon: faLayerGroup },
    { value: 'bottle', label: 'Bottle', icon: faBox },
    { value: 'can', label: 'Can', icon: faBox },
    { value: 'vial', label: 'Vial', icon: faSyringe },
    { value: 'ampoule', label: 'Ampoule', icon: faSyringe },
    { value: 'strip', label: 'Strip', icon: faPills },
    { value: 'sachet', label: 'Sachet', icon: faBoxOpen },
    { value: 'tube', label: 'Tube', icon: faBox }
];

const subPackingTypesMap = {
    box: [
        { value: 'bottles', label: 'Bottles', defaultQty: 24 },
        { value: 'pieces', label: 'Pieces', defaultQty: 50 },
        { value: 'cans', label: 'Cans', defaultQty: 12 },
        { value: 'packs', label: 'Packs', defaultQty: 10 },
        { value: 'tablets', label: 'Tablets', defaultQty: 100 },
        { value: 'capsules', label: 'Capsules', defaultQty: 60 }
    ],
    carton: [
        { value: 'boxes', label: 'Boxes', defaultQty: 6 },
        { value: 'pieces', label: 'Pieces', defaultQty: 100 },
        { value: 'bottles', label: 'Bottles', defaultQty: 48 },
        { value: 'strips', label: 'Strips', defaultQty: 50 }
    ],
    crate: [
        { value: 'bottles', label: 'Bottles', defaultQty: 12 },
        { value: 'cans', label: 'Cans', defaultQty: 24 },
        { value: 'pieces', label: 'Pieces', defaultQty: 30 }
    ],
    packet: [
        { value: 'pieces', label: 'Pieces', defaultQty: 20 },
        { value: 'grams', label: 'Grams', defaultQty: 500 },
        { value: 'tablets', label: 'Tablets', defaultQty: 10 },
        { value: 'capsules', label: 'Capsules', defaultQty: 10 }
    ],
    bundle: [
        { value: 'pieces', label: 'Pieces', defaultQty: 100 },
        { value: 'dozen', label: 'Dozen', defaultQty: 10 }
    ],
    bottle: [
        { value: 'pieces', label: 'Pieces', defaultQty: 1 },
        { value: 'liters', label: 'Liters', defaultQty: 1 },
        { value: 'milliliters', label: 'Milliliters', defaultQty: 500 }
    ],
    can: [
        { value: 'pieces', label: 'Pieces', defaultQty: 1 },
        { value: 'liters', label: 'Liters', defaultQty: 1 }
    ],
    vial: [
        { value: 'pieces', label: 'Pieces', defaultQty: 1 },
        { value: 'milliliters', label: 'Milliliters', defaultQty: 10 }
    ],
    strip: [
        { value: 'tablets', label: 'Tablets', defaultQty: 10 },
        { value: 'capsules', label: 'Capsules', defaultQty: 10 }
    ],
    sachet: [
        { value: 'pieces', label: 'Pieces', defaultQty: 1 },
        { value: 'grams', label: 'Grams', defaultQty: 5 }
    ],
    tube: [
        { value: 'pieces', label: 'Pieces', defaultQty: 1 },
        { value: 'grams', label: 'Grams', defaultQty: 30 }
    ]
};

const unitsOfMeasure = [
    { value: '', label: 'Select Unit' },
    { value: '1_liter', label: '1 Liter (1L)' },
    { value: '1.5_liter', label: '1.5 Liters (1.5L)' },
    { value: '2_liter', label: '2 Liters (2L)' },
    { value: '3_liter', label: '3 Liters (3L)' },
    { value: '5_liter', label: '5 Liters (5L)' },
    { value: '0.5_liter', label: '0.5 Liter (500ml)' },
    { value: '0.7_liter', label: '0.7 Liter (700ml)' },
    { value: '200_ml', label: '200 ml' },
    { value: '250_ml', label: '250 ml' },
    { value: '300_ml', label: '300 ml' },
    { value: '350_ml', label: '350 ml' },
    { value: '400_ml', label: '400 ml' },
    { value: '450_ml', label: '450 ml' },
    { value: '500_ml', label: '500 ml' },
    { value: 'milliliter', label: 'Milliliter (ml)' },
    { value: 'kilogram', label: 'Kilogram (kg)' },
    { value: 'gram', label: 'Gram (g)' },
    { value: 'piece', label: 'Piece (pc)' },
    { value: 'dozen', label: 'Dozen (dz)' },
    { value: 'box', label: 'Box' },
    { value: 'packet', label: 'Packet' },
    { value: 'can', label: 'Can' },
    { value: 'bottle', label: 'Bottle' },
    { value: 'tablet', label: 'Tablet' },
    { value: 'capsule', label: 'Capsule' },
    { value: 'strip', label: 'Strip' },
    { value: 'vial', label: 'Vial' },
    { value: 'ampoule', label: 'Ampoule' },
    { value: 'sachet', label: 'Sachet' },
    { value: 'tube', label: 'Tube' }
];

// ==================== TRANSLATOR ====================
const createPurchasesTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_purchases_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => { cache.set(key, value); });
            }
        } catch (e) {}
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => { cacheObj[key] = value; });
            localStorage.setItem('ourzest_purchases_translations', JSON.stringify(cacheObj));
        } catch (e) {}
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        const dict = {
            'ur': {
                'New Purchase': 'نیا خریداری', 'Edit Purchase': 'خریداری میں ترمیم کریں',
                'Purchase Details': 'خریداری کی تفصیلات', 'Bill Number': 'بل نمبر',
                'Supplier': 'سپلائر', 'Supplier Name': 'سپلائر کا نام', 'Phone': 'فون نمبر',
                'Address': 'پتہ', 'Date': 'تاریخ', 'Bill File': 'بل فائل', 'Items': 'آئٹمز',
                'Add Item': 'آئٹم شامل کریں', 'Product Name': 'پروڈکٹ کا نام',
                'Batch Number': 'بیچ نمبر', 'Price': 'قیمت', 'Quantity': 'مقدار',
                'Total': 'کل', 'Remove': 'ہٹائیں', 'Grand Total': 'کل رقم',
                'Paid Amount': 'ادا کردہ رقم', 'Balance': 'باقی رقم',
                'Save Purchase': 'خریداری محفوظ کریں', 'Update Purchase': 'خریداری اپ ڈیٹ کریں',
                'Clear': 'صاف کریں', 'Save Draft': 'ڈرافٹ محفوظ کریں',
                'Drafts': 'ڈرافٹس', 'Saved Drafts': 'محفوظ کردہ ڈرافٹس',
                'Success': 'کامیابی', 'Error': 'خرابی', 'Warning': 'انتباہ',
                'Search Products': 'پروڈکٹس تلاش کریں',
                'Scanner Ready': 'اسکینر تیار ہے', 'Product Found': 'پروڈکٹ مل گیا',
                'Product Not Found': 'پروڈکٹ نہیں ملا',
                'Color': 'رنگ', 'Size': 'سائز', 'Category': 'زمرہ',
                'Company': 'کمپنی', 'Expiry Date': 'میعاد ختم ہونے کی تاریخ',
                'Product Code': 'پروڈکٹ کوڈ', 'Barcode': 'بار کوڈ'
            }
        };
        if (dict[targetLang] && dict[targetLang][text]) return dict[targetLang][text];
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) return cache.get(cacheKey);
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        addPurchase: 'New Purchase', editPurchase: 'Edit Purchase',
        purchaseDetails: 'Purchase Details', billNumber: 'Bill Number',
        supplier: 'Supplier', supplierName: 'Supplier Name', phone: 'Phone',
        address: 'Address', date: 'Date', billFile: 'Bill File', items: 'Items',
        addItem: 'Add Item', productName: 'Product Name', productCode: 'Product Code',
        batchNumber: 'Batch Number', price: 'Price', quantity: 'Quantity',
        total: 'Total', remove: 'Remove', calculations: 'Calculations',
        subtotal: 'Subtotal', grandTotal: 'Grand Total', paid: 'Paid Amount',
        balance: 'Balance', savePurchase: 'Save Purchase', updatePurchase: 'Update Purchase',
        clear: 'Clear', saving: 'Saving...', purchaseSaved: 'Purchase saved successfully!',
        purchaseUpdated: 'Purchase updated successfully!', failedSave: 'Failed to save purchase',
        failedUpdate: 'Failed to update purchase', networkError: 'Network error occurred',
        billRequired: 'Bill number is required', supplierRequired: 'Supplier is required',
        dateRequired: 'Date is required', itemsRequired: 'At least one item is required',
        productRequired: 'Product name is required', batchRequired: 'Batch number is required',
        priceRequired: 'Price is required', quantityRequired: 'Quantity is required',
        invalidPrice: 'Please enter a valid price', invalidQuantity: 'Please enter a valid quantity',
        invalidPaid: 'Please enter a valid paid amount', success: 'Success', error: 'Error',
        warning: 'Warning', searchSupplier: 'Search supplier...', noSuppliersFound: 'No suppliers found',
        category: 'Category', company: 'Company', packingType: 'Packing Type',
        subPackingType: 'Sub Packing Type', itemsPerPack: 'Items/Pack',
        unitOfMeasure: 'Unit of Measure', costPerPack: 'Cost/Pack',
        optional: 'Optional', browse: 'Browse', generate: 'Generate',
        noFile: 'No file chosen', saveDraft: 'Save Draft', drafts: 'Drafts',
        savedDrafts: 'Saved Drafts', noDraftsSaved: 'No drafts saved',
        draftLoaded: 'Draft loaded', draftCleared: 'Draft cleared',
        clearForm: 'Clear Form', viewDrafts: 'View Drafts', goBack: 'Go Back',
        searchProducts: 'Search Products', purchaseType: 'Type',
        packingPurchase: 'Purchase by Pack', subPackingPurchase: 'Purchase by Unit',
        scannerReady: 'Scanner Ready', productFound: 'Product found!',
        productNotFound: 'Product not found', color: 'Color', size: 'Size',
        expiryDate: 'Expiry Date', barcode: 'Barcode'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        const translations = {};
        const promises = [];
        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(translateText(defaultTexts[key], lang).then(t => { translations[key] = t; }));
            }
        });
        if (promises.length > 0) { await Promise.all(promises); saveToCache(); }
        return translations;
    };

    loadFromCache();
    return { getTranslations, translateText };
};

const purchasesTranslator = createPurchasesTranslator();

// ==================== TOAST NOTIFICATION ====================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };
    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };
    if (!toast.show) return null;
    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon"><FontAwesomeIcon icon={getIconForType(toast.type)} /></div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==================== IOS STYLE TOGGLE SWITCH ====================
const IOSToggleSwitch = ({ checked, onChange, disabled, label, activeLabel, inactiveLabel }) => {
    return (
        <button
            type="button"
            className={`ozui-ios-toggle ${checked ? 'ozui-checked' : ''}`}
            onClick={onChange}
            disabled={disabled}
            role="switch"
            aria-checked={checked}
        >
            <span className="ozui-ios-slider" />
            <span className="ozui-ios-label">
                {checked ? (activeLabel || label || 'Buy by Pack') : (inactiveLabel || label || 'Buy by Unit')}
            </span>
        </button>
    );
};

// ==============================================
// Helper: parse numeric value from bquantity string
// ==============================================
const parseBQuantityNumeric = (bquantityStr) => {
    if (!bquantityStr) return 0;
    const match = bquantityStr.match(/^([\d,\.]+)/);
    if (match) return parseFloat(match[1].replace(/,/g, ''));
    return 0;
};

// ==============================================
// Helper: calculate item line total
// ==============================================
const calculateItemTotal = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    if (item.purchase_type === 'sub_packing') {
        const pricePerUnit = parseFloat(item.price) || 0;
        return (pricePerUnit * quantity).toFixed(2);
    } else {
        const costPerPack = parseFloat(item.cost_per_pack) || parseFloat(item.price) || 0;
        return (costPerPack * quantity).toFixed(2);
    }
};

// ==================== ITEMS TABLE COMPONENT ====================
// ==================== ITEMS TABLE COMPONENT ====================
const ItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    errors,
    disabled,
    translations,
    isRTL,
    user,
    shopId,
    showToast
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [products, setProducts] = useState([]);
    const [filteredProductsByIndex, setFilteredProductsByIndex] = useState({});
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [showProductDropdown, setShowProductDropdown] = useState({});
    const [availableSubPackings, setAvailableSubPackings] = useState({});
    const productSearchRefs = useRef({});

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadProducts = async () => {
        if (!user?.username || !shopId) return;
        try {
            const response = await fetch(PRODUCT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_all_products', username: user?.username, shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') setProducts(data.data);
        } catch (error) { console.error('Error loading products:', error); }
    };

    useEffect(() => { loadProducts(); }, [user?.username, shopId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(productSearchRefs.current).forEach(index => {
                if (productSearchRefs.current[index] && !productSearchRefs.current[index].contains(event.target)) {
                    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        items.forEach((item, index) => {
            if (item.packing_type && subPackingTypesMap[item.packing_type]) {
                setAvailableSubPackings(prev => ({ ...prev, [index]: subPackingTypesMap[item.packing_type] }));
            } else {
                setAvailableSubPackings(prev => ({ ...prev, [index]: [] }));
            }
        });
    }, [items]);

    const handleProductSearch = (index, term) => {
        setProductSearchTerms(prev => ({ ...prev, [index]: term }));
        if (term.trim() === '') {
            setFilteredProductsByIndex(prev => ({ ...prev, [index]: [] }));
            setShowProductDropdown(prev => ({ ...prev, [index]: false }));
        } else {
            const filtered = products.filter(p =>
                p.product_name?.toLowerCase().includes(term.toLowerCase()) ||
                p.batch_no?.toLowerCase().includes(term.toLowerCase()) ||
                p.barcode?.toLowerCase().includes(term.toLowerCase()) ||
                p.product_code?.toLowerCase().includes(term.toLowerCase()) ||
                p.company?.toLowerCase().includes(term.toLowerCase()) ||
                p.category?.toLowerCase().includes(term.toLowerCase())
            );
            setFilteredProductsByIndex(prev => ({ ...prev, [index]: filtered }));
            setShowProductDropdown(prev => ({ ...prev, [index]: true }));
        }
    };

    const buildItemFromProduct = (product) => {
        let itemsPerPack = 1;
        let unitOfMeasure = product.unit_of_measure || 'piece';
        let packingType = product.packing_type || '';
        let subPackingType = product.sub_packing_type || '';
        let costPerPack = 0;
        let sellPerPack = 0;
        let costPerUnit = 0;
        let purchaseType = product.purchase_type || 'packing';

        if (product.packaging_info) {
            try {
                const info = typeof product.packaging_info === 'string'
                    ? JSON.parse(product.packaging_info)
                    : product.packaging_info;
                if (info) {
                    itemsPerPack = parseFloat(info.items_per_pack) || 1;
                    unitOfMeasure = info.unit_of_measure || unitOfMeasure;
                    packingType = info.packing_type || packingType;
                    subPackingType = info.sub_packing_type || subPackingType;
                    costPerPack = parseFloat(info.cost_per_pack) || 0;
                    sellPerPack = parseFloat(info.sell_per_pack) || 0;
                    purchaseType = info.purchase_type || purchaseType;
                }
            } catch (e) {}
        }

        if (product.items_per_pack && parseFloat(product.items_per_pack) > 0) {
            itemsPerPack = parseFloat(product.items_per_pack);
        }
        if (product.packing_type) packingType = product.packing_type;
        if (product.sub_packing_type) subPackingType = product.sub_packing_type;

        if (product.cost_per_pack && parseFloat(product.cost_per_pack) > 0) {
            costPerPack = parseFloat(product.cost_per_pack);
        } else if (costPerPack <= 0 && product.cost_price) {
            costPerPack = parseFloat(product.cost_price) * itemsPerPack;
        }

        if (product.sell_per_pack && parseFloat(product.sell_per_pack) > 0) {
            sellPerPack = parseFloat(product.sell_per_pack);
        }

        if (product.cost_per_unit && parseFloat(product.cost_per_unit) > 0) {
            costPerUnit = parseFloat(product.cost_per_unit);
        } else if (costPerPack > 0 && itemsPerPack > 0) {
            costPerUnit = costPerPack / itemsPerPack;
        } else if (product.cost_price) {
            costPerUnit = parseFloat(product.cost_price);
        }

        if (sellPerPack <= 0 && product.sell_price_per_unit && parseFloat(product.sell_price_per_unit) > 0) {
            sellPerPack = parseFloat(product.sell_price_per_unit) * itemsPerPack;
        }
        if (sellPerPack <= 0 && product.sell_price) {
            sellPerPack = parseFloat(product.sell_price);
        }

        const displayPrice = purchaseType === 'packing'
            ? costPerPack.toFixed(2)
            : costPerUnit.toFixed(2);

        return {
            product_name: product.product_name || '',
            batch_no: product.batch_no || '',
            product_code: product.product_code || '',
            category: product.category || '',
            company: product.company || '',
            color: product.color || '',
            size: product.size || '',
            expiry_date: product.expiry_date || '',
            purchase_type: purchaseType,
            packing_type: packingType,
            sub_packing_type: subPackingType,
            items_per_pack: itemsPerPack,
            unit_of_measure: unitOfMeasure,
            price: displayPrice,
            cost_per_pack: costPerPack > 0 ? costPerPack.toFixed(2) : '',
            sell_per_pack: sellPerPack > 0 ? sellPerPack.toFixed(2) : '',
            quantity: '1',
            product_id: product.id || '',
            _pack_price: costPerPack > 0 ? costPerPack.toFixed(2) : '',
            _unit_price: costPerUnit > 0 ? costPerUnit.toFixed(2) : '',
            available_stock_packs: product.quantity || 0,
            bquantity: product.bquantity || ''
        };
    };

    const selectProduct = (index, product) => {
        const newItem = buildItemFromProduct(product);
        onItemChange(index, 'batch_update', newItem);
        setProductSearchTerms(prev => ({ ...prev, [index]: product.product_name || '' }));
        setFilteredProductsByIndex(prev => ({ ...prev, [index]: [] }));
        setShowProductDropdown(prev => ({ ...prev, [index]: false }));

        const typeLabel = newItem.purchase_type === 'sub_packing' ? 'Sub Packing (Units)' : 'Packing (Packs)';
        const priceLabel = newItem.purchase_type === 'sub_packing'
            ? `Unit price: Rs. ${newItem.price}`
            : `Pack price: Rs. ${newItem.price}`;
        if (showToast) showToast('success', `"${product.product_name}" added — ${typeLabel} | ${priceLabel}`);
    };

    const togglePurchaseType = (index) => {
        const currentItem = items[index];
        const currentType = currentItem.purchase_type || 'packing';
        const newType = currentType === 'packing' ? 'sub_packing' : 'packing';

        let newPrice = '';
        if (newType === 'packing') {
            newPrice = currentItem._pack_price || currentItem.cost_per_pack || '';
        } else {
            newPrice = currentItem._unit_price || '';
            if (!newPrice && currentItem._pack_price && currentItem.items_per_pack) {
                const derived = parseFloat(currentItem._pack_price) / parseFloat(currentItem.items_per_pack);
                if (!isNaN(derived) && isFinite(derived)) newPrice = derived.toFixed(2);
            }
        }

        onItemChange(index, 'batch_update', {
            purchase_type: newType,
            price: newPrice
        });

        if (showToast) {
            const msg = newType === 'packing'
                ? `Pack Purchase — price: Rs. ${newPrice} per pack`
                : `Unit Purchase — price: Rs. ${newPrice} per unit`;
            showToast('info', msg);
        }
    };

    const handlePackPriceChange = (index, value) => {
        const rounded = value ? parseFloat(value).toFixed(2) : '';
        const item = items[index];
        const itemsPerPack = parseFloat(item.items_per_pack) || 1;
        const updates = {
            cost_per_pack: rounded,
            _pack_price: rounded
        };
        if ((item.purchase_type || 'packing') === 'packing') {
            updates.price = rounded;
        }
        if (value && itemsPerPack > 0) {
            const perUnit = parseFloat(value) / itemsPerPack;
            if (!isNaN(perUnit) && isFinite(perUnit)) {
                updates._unit_price = perUnit.toFixed(2);
            }
        }
        onItemChange(index, 'batch_update', updates);
    };

    const handleItemsPerPackChange = (index, value) => {
        const itemsPerPack = parseFloat(value) || 1;
        const item = items[index];
        const updates = { items_per_pack: itemsPerPack };
        if (item.cost_per_pack && itemsPerPack > 0) {
            const perUnit = parseFloat(item.cost_per_pack) / itemsPerPack;
            if (!isNaN(perUnit) && isFinite(perUnit)) {
                updates._unit_price = perUnit.toFixed(2);
                if ((item.purchase_type || 'packing') === 'sub_packing') {
                    updates.price = perUnit.toFixed(2);
                }
            }
        }
        onItemChange(index, 'batch_update', updates);
    };

    const handlePackingTypeChange = (index, packingType) => {
        onItemChange(index, 'batch_update', {
            packing_type: packingType,
            sub_packing_type: '',
            items_per_pack: '',
            unit_of_measure: '',
            cost_per_pack: ''
        });
    };

    const handleSubPackingChange = (index, subPackingType) => {
        const subOpt = availableSubPackings[index]?.find(sp => sp.value === subPackingType);
        const updates = { sub_packing_type: subPackingType };
        if (subOpt?.defaultQty) updates.items_per_pack = subOpt.defaultQty;
        onItemChange(index, 'batch_update', updates);
    };

    const isPackingMode = (item) => (item.purchase_type || 'packing') === 'packing';

    const getStockDisplay = (item) => {
        const packStock = parseFloat(item.available_stock_packs || item.quantity || 0);
        const unitStock = parseBQuantityNumeric(item.bquantity || '0');
        if (isPackingMode(item)) {
            return `${packStock.toFixed(0)} ${item.packing_type || 'packs'} in stock`;
        }
        return `${unitStock.toFixed(2)} ${item.sub_packing_type || item.unit_of_measure || 'units'} in stock`;
    };

    if (!isMobile) {
        return (
            <div className="ozui-items-table-container">
                <div className="ozui-table-header-actions">
                    <h4><FontAwesomeIcon icon={faBoxes} />{translations?.items || 'Items'}</h4>
                    <button type="button" className="ozui-btn-add-item" onClick={onAdd} disabled={disabled}>
                        <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                    </button>
                </div>
                <div className="ozui-table-wrapper">
                    <table className="ozui-items-table">
                        <thead>
                            <tr>
                                <th>{translations?.productName || 'Product'} <span className="ozui-required">*</span></th>
                                <th>{translations?.batchNumber || 'Batch'} <span className="ozui-required">*</span></th>
                                <th>{translations?.purchaseType || 'Type'}</th>
                                <th>Cost/Pack</th>
                                <th>Items/Pack</th>
                                <th>{translations?.company || 'Company'}</th>
                                <th>Price <span className="ozui-required">*</span></th>
                                <th>Qty <span className="ozui-required">*</span></th>
                                <th>{translations?.expiryDate || 'Expiry'}</th>
                                <th>Stock</th>
                                <th>{translations?.total || 'Total'}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => {
                                const filteredForIndex = filteredProductsByIndex[index] || [];
                                const showDrop = showProductDropdown[index] || false;
                                const searchTerm = productSearchTerms[index] !== undefined
                                    ? productSearchTerms[index]
                                    : item.product_name;
                                const isPacking = isPackingMode(item);
                                const itemTotal = calculateItemTotal(item);
                                const packLabel = item.packing_type || 'Pack';
                                const unitLabel = item.sub_packing_type || item.unit_of_measure || 'Unit';

                                return (
                                    <tr key={index} className={errors[`product_name_${index}`] ? 'ozui-error-row' : ''}>
                                        <td>
                                            <div className="ozui-table-field" ref={el => productSearchRefs.current[index] = el}>
                                                <div className="ozui-product-search-container">
                                                    <input
                                                        type="text"
                                                        value={searchTerm}
                                                        onChange={e => handleProductSearch(index, e.target.value)}
                                                        onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))}
                                                        placeholder="Search product..."
                                                        disabled={disabled}
                                                        autoComplete="off"
                                                        className={errors[`product_name_${index}`] ? 'ozui-error' : ''}
                                                    />
                                                    {showDrop && filteredForIndex.length > 0 && (
                                                        <div className="ozui-product-dropdown-table">
                                                            {filteredForIndex.map(product => (
                                                                <div key={product.id} className="ozui-product-option" onClick={() => selectProduct(index, product)}>
                                                                    <div className="ozui-product-name">{product.product_name}</div>
                                                                    <div className="ozui-product-details">
                                                                        {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> {product.batch_no}</span>}
                                                                        {product.product_code && <span><FontAwesomeIcon icon={faBarcode} /> {product.product_code}</span>}
                                                                        {product.color && <span><FontAwesomeIcon icon={faPallet} /> {product.color}</span>}
                                                                        {product.expiry_date && <span><FontAwesomeIcon icon={faCalendarAlt} /> {product.expiry_date}</span>}
                                                                    </div>
                                                                    <div className="ozui-product-item-price-stock">
                                                                        <span className="ozui-product-price">
                                                                            <FontAwesomeIcon icon={faRupeeSign} />
                                                                            {' '}
                                                                            {(product.purchase_type === 'sub_packing'
                                                                                ? parseFloat(product.cost_per_unit || product.cost_price || 0)
                                                                                : parseFloat(product.cost_per_pack || product.cost_price || 0)
                                                                            ).toFixed(2)}
                                                                            {' '}
                                                                            {product.purchase_type === 'sub_packing' ? '/unit' : '/pack'}
                                                                        </span>
                                                                        <span className={`ozui-product-stock ${parseFloat(product.quantity) <= (product.quantity_alert || 5) ? 'ozui-low-stock' : ''}`}>
                                                                            Stock: {product.quantity} packs / {parseBQuantityNumeric(product.bquantity || '0').toFixed(2)} units
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                {errors[`product_name_${index}`] && <div className="ozui-table-error">{errors[`product_name_${index}`]}</div>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="ozui-table-field">
                                                <input
                                                    type="text"
                                                    value={item.batch_no || ''}
                                                    onChange={e => onItemChange(index, 'batch_no', e.target.value)}
                                                    placeholder="Batch no"
                                                    disabled={disabled}
                                                    className={errors[`batch_no_${index}`] ? 'ozui-error' : ''}
                                                    style={{ backgroundColor: item.batch_no ? '#e8f5e9' : '' }}
                                                />
                                                {errors[`batch_no_${index}`] && <div className="ozui-table-error">{errors[`batch_no_${index}`]}</div>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="ozui-table-field">
                                                <IOSToggleSwitch
                                                    checked={isPacking}
                                                    onChange={() => togglePurchaseType(index)}
                                                    disabled={disabled}
                                                    activeLabel={`Buy by ${packLabel}`}
                                                    inactiveLabel={`Buy by ${unitLabel}`}
                                                />
                                            </div>
                                        </td>

                                        <td>
                                            <div className="ozui-table-field">
                                                {isPacking ? (
                                                    <input
                                                        type="number"
                                                        value={item.cost_per_pack || ''}
                                                        onChange={e => handlePackPriceChange(index, e.target.value)}
                                                        step="0.01"
                                                        min="0"
                                                        disabled={disabled}
                                                        placeholder="0.00"
                                                        className={errors[`cost_per_pack_${index}`] ? 'ozui-error' : ''}
                                                    />
                                                ) : (
                                                    <div className="ozui-disabled-placeholder" style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</div>
                                                )}
                                                {errors[`cost_per_pack_${index}`] && <div className="ozui-table-error">{errors[`cost_per_pack_${index}`]}</div>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="ozui-table-field">
                                                {isPacking ? (
                                                    <input
                                                        type="number"
                                                        value={item.items_per_pack || ''}
                                                        onChange={e => handleItemsPerPackChange(index, e.target.value)}
                                                        step="1"
                                                        min="1"
                                                        disabled={disabled}
                                                        placeholder="1"
                                                    />
                                                ) : (
                                                    <div className="ozui-disabled-placeholder" style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</div>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="ozui-table-field">
                                                <input
                                                    type="text"
                                                    value={item.company || ''}
                                                    onChange={e => onItemChange(index, 'company', e.target.value)}
                                                    disabled={disabled}
                                                    placeholder="Company"
                                                />
                                            </div>
                                        </td>

                                        <td>
                                            <div className="ozui-table-field">
                                                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>
                                                    {isPacking ? 'Per Pack' : 'Per Unit'}
                                                </div>
                                                <input
                                                    type="number"
                                                    value={item.price}
                                                    onChange={e => onItemChange(index, 'price', e.target.value)}
                                                    step="0.01"
                                                    min="0"
                                                    disabled={disabled}
                                                    className={errors[`price_${index}`] ? 'ozui-error' : ''}
                                                    placeholder="0.00"
                                                />
                                                {errors[`price_${index}`] && <div className="ozui-table-error">{errors[`price_${index}`]}</div>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="ozui-table-field">
                                                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>
                                                    {isPacking ? packLabel + 's' : unitLabel + 's'}
                                                </div>
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={e => onItemChange(index, 'quantity', e.target.value)}
                                                    step="0.001"
                                                    min="0.001"
                                                    disabled={disabled}
                                                    className={errors[`quantity_${index}`] ? 'ozui-error' : ''}
                                                    placeholder="0"
                                                />
                                                {errors[`quantity_${index}`] && <div className="ozui-table-error">{errors[`quantity_${index}`]}</div>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="ozui-table-field">
                                                <input
                                                    type="date"
                                                    value={item.expiry_date || ''}
                                                    onChange={e => onItemChange(index, 'expiry_date', e.target.value)}
                                                    disabled={disabled}
                                                    className={errors[`expiry_date_${index}`] ? 'ozui-error' : ''}
                                                    style={{ padding: '4px 8px', fontSize: '0.85rem' }}
                                                />
                                                {errors[`expiry_date_${index}`] && <div className="ozui-table-error">{errors[`expiry_date_${index}`]}</div>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="ozui-table-field">
                                                <span className="ozui-stock-badge" style={{ fontSize: '0.75rem' }}>
                                                    {getStockDisplay(item)}
                                                </span>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="ozui-table-field">
                                                <input type="text" value={itemTotal} readOnly disabled className="ozui-readonly-input ozui-total-display" />
                                            </div>
                                        </td>

                                        <td>
                                            <button
                                                type="button"
                                                className="ozui-table-remove-btn"
                                                onClick={() => onRemove(index)}
                                                disabled={disabled || items.length === 1}
                                                title="Remove"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // MOBILE VIEW
    return (
        <div className="ozui-items-mobile-container">
            <div className="ozui-items-header-mobile">
                <h4><FontAwesomeIcon icon={faBoxes} />{translations?.items || 'Items'}</h4>
                <button type="button" className="ozui-btn-add-item-mobile" onClick={onAdd} disabled={disabled}>
                    <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="ozui-items-cards-container">
                {items.map((item, index) => {
                    const filteredForIndex = filteredProductsByIndex[index] || [];
                    const showDrop = showProductDropdown[index] || false;
                    const searchTerm = productSearchTerms[index] !== undefined
                        ? productSearchTerms[index]
                        : item.product_name;
                    const isPacking = isPackingMode(item);
                    const itemTotal = calculateItemTotal(item);
                    const packLabel = item.packing_type || 'Pack';
                    const unitLabel = item.sub_packing_type || item.unit_of_measure || 'Unit';

                    return (
                        <div key={index} className={`ozui-item-card-mobile ${isPacking ? 'ozui-packing-card' : 'ozui-subpacking-card'}`}>
                            <div className="ozui-item-card-header">
                                <span className="ozui-item-number">Item {index + 1}</span>
                                <div className="ozui-sell-method-toggle-mobile">
                                    <span className={`ozui-method-badge ${isPacking ? 'ozui-packing' : 'ozui-subpacking'}`}>
                                        {isPacking ? packLabel : unitLabel}
                                    </span>
                                    <button type="button" className="ozui-toggle-method-btn" onClick={() => togglePurchaseType(index)} disabled={disabled}>
                                        <FontAwesomeIcon icon={isPacking ? faToggleOn : faToggleOff} />
                                    </button>
                                </div>
                                <button type="button" className="ozui-item-remove-btn-mobile" onClick={() => onRemove(index)} disabled={disabled || items.length === 1}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                            <div className="ozui-item-card-content">
                                <div className={`ozui-mobile-field ${errors[`product_name_${index}`] ? 'ozui-has-error' : ''}`} ref={el => productSearchRefs.current[index] = el}>
                                    <label>{translations?.productName || 'Product'} <span className="required">*</span></label>
                                    <div className="ozui-product-search-container">
                                        <input type="text" value={searchTerm} onChange={e => handleProductSearch(index, e.target.value)} onFocus={() => setShowProductDropdown(prev => ({ ...prev, [index]: true }))} placeholder="Search product..." disabled={disabled} autoComplete="off" />
                                        {showDrop && filteredForIndex.length > 0 && (
                                            <div className="ozui-product-dropdown-mobile">
                                                {filteredForIndex.map(product => (
                                                    <div key={product.id} className="ozui-product-option" onClick={() => selectProduct(index, product)}>
                                                        <div className="ozui-product-name">{product.product_name}</div>
                                                        <div className="ozui-product-details">{product.batch_no && <span><FontAwesomeIcon icon={faTag} /> {product.batch_no}</span>}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {errors[`product_name_${index}`] && <div className="ozui-error-text">{errors[`product_name_${index}`]}</div>}
                                </div>
                                <div className={`ozui-mobile-field ${errors[`batch_no_${index}`] ? 'ozui-has-error' : ''}`}>
                                    <label>{translations?.batchNumber || 'Batch'} <span className="required">*</span></label>
                                    <input type="text" value={item.batch_no || ''} onChange={e => onItemChange(index, 'batch_no', e.target.value)} placeholder="Batch number" disabled={disabled} />
                                    {errors[`batch_no_${index}`] && <div className="ozui-error-text">{errors[`batch_no_${index}`]}</div>}
                                </div>
                                {isPacking && (
                                    <>
                                        <div className="ozui-mobile-field"><label>Packing Type</label><select value={item.packing_type || ''} onChange={e => handlePackingTypeChange(index, e.target.value)} disabled={disabled}>{packingTypesList.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
                                        <div className="ozui-mobile-field"><label>Sub Packing Type</label><select value={item.sub_packing_type || ''} onChange={e => handleSubPackingChange(index, e.target.value)} disabled={disabled || !item.packing_type}><option value="">Select</option>{(availableSubPackings[index] || []).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
                                        <div className="ozui-mobile-field"><label>Items Per Pack</label><input type="number" value={item.items_per_pack || ''} onChange={e => handleItemsPerPackChange(index, e.target.value)} step="1" min="1" disabled={disabled} /></div>
                                        <div className="ozui-mobile-field"><label>Unit of Measure</label><select value={item.unit_of_measure || ''} onChange={e => onItemChange(index, 'unit_of_measure', e.target.value)} disabled={disabled}>{unitsOfMeasure.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}</select></div>
                                        <div className="ozui-mobile-field"><label>Cost Per Pack</label><input type="number" value={item.cost_per_pack || ''} onChange={e => handlePackPriceChange(index, e.target.value)} step="0.01" min="0" disabled={disabled} placeholder="0.00" /></div>
                                    </>
                                )}
                                {!isPacking && (
                                    <div className="ozui-info-message"><FontAwesomeIcon icon={faInfoCircle} /><span>Sub Packing: adds to Bulk Quantity (BQuantity) only</span></div>
                                )}
                                <div className="ozui-mobile-field"><label>Company</label><input type="text" value={item.company || ''} onChange={e => onItemChange(index, 'company', e.target.value)} disabled={disabled} /></div>
                                <div className="ozui-mobile-row">
                                    <div className={`ozui-mobile-field half ${errors[`price_${index}`] ? 'ozui-has-error' : ''}`}><label>{isPacking ? 'Price/Pack' : 'Price/Unit'} <span className="required">*</span></label><input type="number" value={item.price} onChange={e => onItemChange(index, 'price', e.target.value)} step="0.01" min="0" disabled={disabled} placeholder="0.00" />{errors[`price_${index}`] && <div className="ozui-error-text">{errors[`price_${index}`]}</div>}</div>
                                    <div className={`ozui-mobile-field half ${errors[`quantity_${index}`] ? 'ozui-has-error' : ''}`}><label>{isPacking ? `Qty (${packLabel}s)` : `Qty (${unitLabel}s)`} <span className="required">*</span></label><input type="number" value={item.quantity} onChange={e => onItemChange(index, 'quantity', e.target.value)} step="0.001" min="0.001" disabled={disabled} placeholder="0" />{errors[`quantity_${index}`] && <div className="ozui-error-text">{errors[`quantity_${index}`]}</div>}</div>
                                </div>
                                <div className="ozui-mobile-field">
                                    <label>{translations?.expiryDate || 'Expiry Date'}</label>
                                    <input type="date" value={item.expiry_date || ''} onChange={e => onItemChange(index, 'expiry_date', e.target.value)} disabled={disabled} />
                                </div>
                                <div className="ozui-mobile-field"><label>Current Stock</label><input type="text" value={getStockDisplay(item)} readOnly className="ozui-readonly-input" /></div>
                                <div className="ozui-mobile-field"><label>{translations?.total || 'Total'}</label><input type="text" value={itemTotal} readOnly disabled className="ozui-readonly-input ozui-total-display" /></div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ==================== MAIN PURCHASES COMPONENT ====================
const PharmacyPurchases = ({ purchaseId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const initialFormData = {
        date: new Date().toISOString().split('T')[0],
        bill_no: '',
        supplierid: '',
        supplier_name: '',
        phone: '',
        address: '',
        bill_file: null,
        grand_total: '0.00',
        paid: '0.00',
        balance: '0.00',
        items: []
    };

    const [formData, setFormData] = useState(initialFormData);
    const [suppliers, setSuppliers] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);
    const [globalProductSearchTerm, setGlobalProductSearchTerm] = useState('');
    const [filteredGlobalProducts, setFilteredGlobalProducts] = useState([]);
    const [showGlobalProductDropdown, setShowGlobalProductDropdown] = useState(false);
    const [searchingGlobalProducts, setSearchingGlobalProducts] = useState(false);

    // Barcode Scanner State
    const [isScannerActive, setIsScannerActive] = useState(true);
    const [lastScannedBarcode, setLastScannedBarcode] = useState('');
    const barcodeInputRef = useRef(null);
    const scanCooldownRef = useRef(false);
    const scanTimerRef = useRef(null);

    const productSearchRef = useRef(null);
    const billNumberRef = useRef(null);
    const toastTimerRef = useRef(null);
    const supplierSearchRef = useRef(null);
    const fileInputRef = useRef(null);
    const isSavingRef = useRef(false);
    const isClearingRef = useRef(false);
    const isInitialLoadRef = useRef(true);
    const draftsDropdownRef = useRef(null);

    // ==============================================
    // GLOBAL BARCODE SCANNER
    // ==============================================
    useEffect(() => {
        let buffer = '';
        let lastTime = 0;

        const handler = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
                if (e.target === barcodeInputRef.current) return;
                return;
            }
            const now = Date.now();
            if (now - lastTime > 50) buffer = '';
            lastTime = now;
            if (e.key.length === 1) buffer += e.key;
            if (e.key === 'Enter' && buffer.length >= 6) {
                e.preventDefault();
                e.stopPropagation();
                const code = buffer.trim();
                buffer = '';
                if (code && isScannerActive) processBarcodeScan(code);
            }
            clearTimeout(scanTimerRef.current);
            scanTimerRef.current = setTimeout(() => { buffer = ''; }, 100);
        };

        document.addEventListener('keypress', handler, true);
        return () => {
            document.removeEventListener('keypress', handler, true);
            clearTimeout(scanTimerRef.current);
        };
    }, [isScannerActive, formData.items]);

    // ==============================================
    // Process Barcode Scan
    // ==============================================
    const processBarcodeScan = useCallback(async (barcode) => {
        if (scanCooldownRef.current) return;
        scanCooldownRef.current = true;
        setLastScannedBarcode(barcode);

        if (!user?.username || !currentShop?.shop_id) {
            showToast('error', 'No shop selected');
            scanCooldownRef.current = false;
            return;
        }

        try {
            const r = await fetch(PRODUCT_API, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_products', searchTerm: barcode, username: user.username, shop_id: currentShop.shop_id })
            });
            const d = await r.json();

            if (d.status === 'success' && d.data && d.data.length > 0) {
                const exactMatch = d.data.find(p => p.barcode === barcode || p.product_code === barcode);
                const rawProduct = exactMatch || d.data[0];

                let itemsPerPack = parseFloat(rawProduct.items_per_pack) || 1;
                let unitOfMeasure = rawProduct.unit_of_measure || 'piece';
                let packingType = rawProduct.packing_type || null;
                let subPackingType = rawProduct.sub_packing_type || null;
                let costPerPack = parseFloat(rawProduct.cost_per_pack) || 0;
                let costPerUnit = parseFloat(rawProduct.cost_per_unit) || 0;
                let sellPerPack = parseFloat(rawProduct.sell_per_pack) || 0;
                let purchaseType = rawProduct.purchase_type || 'packing';

                if (rawProduct.packaging_info) {
                    try {
                        const info = typeof rawProduct.packaging_info === 'string' ? JSON.parse(rawProduct.packaging_info) : rawProduct.packaging_info;
                        if (info) {
                            itemsPerPack = parseFloat(info.items_per_pack) || itemsPerPack;
                            unitOfMeasure = info.unit_of_measure || unitOfMeasure;
                            packingType = info.packing_type || packingType;
                            subPackingType = info.sub_packing_type || subPackingType;
                            if (!costPerPack && info.cost_per_pack) costPerPack = parseFloat(info.cost_per_pack);
                            if (!sellPerPack && info.sell_per_pack) sellPerPack = parseFloat(info.sell_per_pack);
                            purchaseType = info.purchase_type || purchaseType;
                        }
                    } catch (e) {}
                }

                if (!costPerUnit && costPerPack > 0 && itemsPerPack > 0) costPerUnit = costPerPack / itemsPerPack;
                if (!costPerUnit && rawProduct.cost_price) costPerUnit = parseFloat(rawProduct.cost_price);
                if (!costPerPack && costPerUnit > 0) costPerPack = costPerUnit * itemsPerPack;

                const processedProduct = {
                    id: rawProduct.id, product_name: rawProduct.product_name, product_code: rawProduct.product_code || '',
                    batch_no: rawProduct.batch_no || '', barcode: rawProduct.barcode || '',
                    sell_price: parseFloat(rawProduct.sell_price) || 0,
                    quantity: parseFloat(rawProduct.quantity) || 0,
                    bquantity: rawProduct.bquantity || '',
                    quantity_alert: parseFloat(rawProduct.quantity_alert) || 5,
                    category: rawProduct.category || '', company: rawProduct.company || '',
                    color: rawProduct.color || '', size: rawProduct.size || '',
                    expiry_date: rawProduct.expiry_date || '',
                    packing_type: packingType, sub_packing_type: subPackingType,
                    items_per_pack: itemsPerPack, unit_of_measure: unitOfMeasure,
                    cost_price: parseFloat(rawProduct.cost_price) || 0,
                    cost_per_pack: costPerPack, cost_per_unit: costPerUnit,
                    sell_per_pack: sellPerPack, sell_price_per_unit: parseFloat(rawProduct.sell_price_per_unit) || 0,
                    purchase_type: purchaseType, packaging_info: rawProduct.packaging_info
                };

                addProductToItems(processedProduct);
                showToast('success', `${rawProduct.product_name} - ${translations?.productFound || 'Product found!'}`);
            } else {
                showToast('error', `${translations?.productNotFound || 'Product not found'}: ${barcode}`);
            }
        } catch (error) {
            console.error('Barcode search error:', error);
            showToast('error', 'Error searching product');
        }

        setTimeout(() => { scanCooldownRef.current = false; }, 500);
    }, [user?.username, currentShop?.shop_id, translations]);

    useEffect(() => {
        const handleTabKey = (e) => {
            if (e.key === 'Tab') {
                setShowSupplierDropdown(false);
                setShowGlobalProductDropdown(false);
                setShowDraftsList(false);
            }
        };
        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, []);

    useEffect(() => {
        setTimeout(() => { if (billNumberRef.current) billNumberRef.current.focus(); }, 100);
    }, []);

    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `purchase_draft_${userId}_${shopId}`;
    };

    const saveDraft = () => {
        const hasData = formData.bill_no?.trim() || formData.supplierid || formData.items?.some(i => i.product_name?.trim());
        if (!hasData) { showToast('warning', 'Cannot save empty draft'); return; }
        const draft = { id: Date.now(), ...formData, savedAt: new Date().toISOString(), shop_id: currentShop?.shop_id };
        const existing = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        existing.unshift(draft);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(existing.slice(0, 20)));
        showToast('success', 'Draft saved!');
        loadDrafts();
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        setSavedDrafts(currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts);
    };

    const loadDraft = (draft) => {
        setFormData({ ...initialFormData, ...draft, date: draft.date || new Date().toISOString().split('T')[0], items: draft.items || [] });
        if (draft.supplier_name) setSearchTerm(draft.supplier_name);
        setShowDraftsList(false);
        showToast('success', 'Draft loaded!');
        setTimeout(() => { if (billNumberRef.current) billNumberRef.current.focus(); }, 100);
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(drafts.filter(d => d.id !== draftId)));
        loadDrafts();
        showToast('success', 'Draft deleted!');
    };

    const clearDraft = () => {
        if (isSavingRef.current) return;
        isClearingRef.current = true;
        setFormData({ ...initialFormData, items: [] });
        setSearchTerm('');
        setErrors({});
        setIsEditMode(false);
        setGlobalProductSearchTerm('');
        setLastScannedBarcode('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        showToast('success', 'Form cleared');
        setTimeout(() => { if (billNumberRef.current) billNumberRef.current.focus(); }, 100);
        setTimeout(() => { isClearingRef.current = false; }, 500);
    };

    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        applyTheme(localStorage.getItem('ourzest_theme') || '#6366f1');
        const hs = (e) => { if (e.key === 'ourzest_theme') applyTheme(e.newValue || '#6366f1'); };
        const ht = (e) => { if (e.detail?.theme) applyTheme(e.detail.theme); };
        window.addEventListener('storage', hs);
        window.addEventListener('themeChanged', ht);
        return () => { window.removeEventListener('storage', hs); window.removeEventListener('themeChanged', ht); };
    }, [applyTheme]);

    useEffect(() => {
        const sync = () => {
            const dark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(dark);
            document.body.classList.toggle('ozui-dark', dark);
            document.documentElement.classList.toggle('ozui-dark', dark);
        };
        sync();
        const hs = (e) => { if (e.key === 'ourzest_darkMode') sync(); };
        window.addEventListener('storage', hs);
        window.addEventListener('darkModeToggled', sync);
        return () => { window.removeEventListener('storage', hs); window.removeEventListener('darkModeToggled', sync); };
    }, []);

    useEffect(() => {
        const load = async (lang) => { setTranslations(await purchasesTranslator.getTranslations(lang)); };
        load(currentLanguage);
        const hs = () => { const l = localStorage.getItem('ourzest_language') || 'en'; if (l !== currentLanguage) setCurrentLanguage(l); };
        const hc = (e) => { if (e.detail?.language && e.detail.language !== currentLanguage) setCurrentLanguage(e.detail.language); };
        window.addEventListener('storage', hs);
        window.addEventListener('languageChanged', hc);
        const iv = setInterval(() => {
            const l = localStorage.getItem('ourzest_language') || 'en';
            if (l !== currentLanguage) setCurrentLanguage(l);
        }, 1000);
        return () => { window.removeEventListener('storage', hs); window.removeEventListener('languageChanged', hc); clearInterval(iv); };
    }, [currentLanguage]);

    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [currentLanguage]);

    useEffect(() => { loadDrafts(); }, [currentShop]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(e.target)) setShowDraftsList(false);
            if (supplierSearchRef.current && !supplierSearchRef.current.contains(e.target)) setShowSupplierDropdown(false);
            if (productSearchRef.current && !productSearchRef.current.contains(e.target)) setShowGlobalProductDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => setToast({ show: false, type: '', message: '' }), 3000);
    }, []);

    useEffect(() => { return () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }; }, []);

   const loadSuppliers = async () => {
    if (!user?.username || !currentShop?.shop_id) {
        console.log('⚠️ Missing user or shop:', { username: user?.username, shop_id: currentShop?.shop_id });
        return;
    }
    try {
        console.log('🔍 Fetching suppliers with:', {
            action: 'search_suppliers',
            searchTerm: '',
            username: user.username,
            shop_id: currentShop.shop_id
        });
        
        const r = await fetch(SUPPLIER_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                action: 'search_suppliers', 
                searchTerm: '', 
                username: user.username, 
                shop_id: currentShop.shop_id 
            })
        });
        
        console.log('📡 Response status:', r.status, r.statusText);
        
        const d = await r.json();
        console.log('📦 Response data:', d);
        
        if (d.status === 'success' && d.data) { 
            console.log('✅ Suppliers loaded:', d.data.length);
            setSuppliers(d.data); 
            setFilteredSuppliers(d.data); 
        } else {
            console.warn('⚠️ Unexpected response:', d);
            // Try with 'suppliers' key as fallback (for 'list' action)
            if (d.suppliers) {
                console.log('🔄 Using suppliers key instead');
                setSuppliers(d.suppliers);
                setFilteredSuppliers(d.suppliers);
            }
        }
    } catch (e) { 
        console.error('❌ Error loading suppliers:', e); 
    }
};
    useEffect(() => { loadSuppliers(); }, [user?.username, currentShop?.shop_id]);
    useEffect(() => { if (purchaseId) { setIsEditMode(true); loadPurchaseData(purchaseId); } }, [purchaseId]);
    useEffect(() => { calculateTotals(); }, [formData.items, formData.paid]);

    const loadPurchaseData = async (id) => {
        setLoading(true);
        try {
            const r = await fetch(PURCHASE_API, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id, username: user?.username, shop_id: currentShop?.shop_id })
            });
            const d = await r.json();
            if (d.status === 'success' && d.data) {
                const p = d.data;
                setFormData({
                    date: p.date || new Date().toISOString().split('T')[0],
                    bill_no: p.bill_no || '', supplierid: p.supplierid || '',
                    supplier_name: p.supplier_name || '', phone: p.phone || '',
                    address: p.address || '', bill_file: null,
                    grand_total: p.grand_total || '0.00', paid: p.paid || '0.00',
                    balance: p.balance || '0.00',
                    items: (p.items || []).map(item => ({
                        ...item,
                        purchase_type: item.purchase_type || 'packing',
                        _pack_price: item.purchase_type === 'packing'
                            ? (item.cost_per_pack || item.price || '')
                            : String(parseFloat(item.price || 0) * parseFloat(item.items_per_pack || 1)),
                        _unit_price: item.purchase_type === 'sub_packing'
                            ? (item.price || '')
                            : String(parseFloat(item.cost_per_pack || 0) / parseFloat(item.items_per_pack || 1))
                    }))
                });
                if (p.supplier_name) setSearchTerm(p.supplier_name);
            } else {
                showToast('error', d.message || 'Failed to load purchase data');
            }
        } catch (e) { showToast('error', 'Network error'); }
        finally { setLoading(false); }
    };

    const searchGlobalProducts = useCallback(async (term) => {
        if (!term || term.length < 2 || !user?.username || !currentShop?.shop_id) {
            setFilteredGlobalProducts([]); setShowGlobalProductDropdown(false); return;
        }
        setSearchingGlobalProducts(true);
        try {
            const r = await fetch(PRODUCT_API, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_products', searchTerm: term, username: user.username, shop_id: currentShop.shop_id })
            });
            const d = await r.json();
            if (d.status === 'success') {
                const products = (d.data || []).map(p => {
                    let itemsPerPack = parseFloat(p.items_per_pack) || 1;
                    let unitOfMeasure = p.unit_of_measure || 'piece';
                    let packingType = p.packing_type || null;
                    let subPackingType = p.sub_packing_type || null;
                    let costPerPack = parseFloat(p.cost_per_pack) || 0;
                    let costPerUnit = parseFloat(p.cost_per_unit) || 0;
                    let sellPerPack = parseFloat(p.sell_per_pack) || 0;
                    let purchaseType = p.purchase_type || 'packing';

                    if (p.packaging_info) {
                        try {
                            const info = typeof p.packaging_info === 'string' ? JSON.parse(p.packaging_info) : p.packaging_info;
                            if (info) {
                                itemsPerPack = parseFloat(info.items_per_pack) || itemsPerPack;
                                unitOfMeasure = info.unit_of_measure || unitOfMeasure;
                                packingType = info.packing_type || packingType;
                                subPackingType = info.sub_packing_type || subPackingType;
                                if (!costPerPack && info.cost_per_pack) costPerPack = parseFloat(info.cost_per_pack);
                                if (!sellPerPack && info.sell_per_pack) sellPerPack = parseFloat(info.sell_per_pack);
                                purchaseType = info.purchase_type || purchaseType;
                            }
                        } catch (e) {}
                    }

                    if (!costPerUnit && costPerPack > 0 && itemsPerPack > 0) costPerUnit = costPerPack / itemsPerPack;
                    if (!costPerUnit && p.cost_price) costPerUnit = parseFloat(p.cost_price);
                    if (!costPerPack && costPerUnit > 0) costPerPack = costPerUnit * itemsPerPack;

                    return {
                        id: p.id, product_name: p.product_name, product_code: p.product_code || '',
                        batch_no: p.batch_no || '', barcode: p.barcode || '',
                        sell_price: parseFloat(p.sell_price) || 0,
                        quantity: parseFloat(p.quantity) || 0,
                        bquantity: p.bquantity || '',
                        quantity_alert: parseFloat(p.quantity_alert) || 5,
                        category: p.category || '', company: p.company || '',
                        color: p.color || '', size: p.size || '',
                        expiry_date: p.expiry_date || '',
                        packing_type: packingType, sub_packing_type: subPackingType,
                        items_per_pack: itemsPerPack, unit_of_measure: unitOfMeasure,
                        cost_price: parseFloat(p.cost_price) || 0,
                        cost_per_pack: costPerPack, cost_per_unit: costPerUnit,
                        sell_per_pack: sellPerPack, sell_price_per_unit: parseFloat(p.sell_price_per_unit) || 0,
                        purchase_type: purchaseType, packaging_info: p.packaging_info
                    };
                });
                setFilteredGlobalProducts(products);
                setShowGlobalProductDropdown(products.length > 0);
            } else {
                setFilteredGlobalProducts([]); setShowGlobalProductDropdown(false);
            }
        } catch (e) {
            console.error('Product search error:', e);
            setFilteredGlobalProducts([]); setShowGlobalProductDropdown(false);
        } finally { setSearchingGlobalProducts(false); }
    }, [user?.username, currentShop?.shop_id]);

    const addProductToItems = (product) => {
        const existingIndex = formData.items.findIndex(i => i.product_id === product.id);
        if (existingIndex >= 0) {
            const updated = [...formData.items];
            updated[existingIndex] = { ...updated[existingIndex], quantity: String(parseFloat(updated[existingIndex].quantity || 0) + 1) };
            setFormData(prev => ({ ...prev, items: updated }));
            showToast('success', `Added another ${product.product_name}`);
        } else {
            const itemsPerPack = parseFloat(product.items_per_pack) || 1;
            const unitOfMeasure = product.unit_of_measure || 'piece';
            const packingType = product.packing_type || '';
            const subPackingType = product.sub_packing_type || '';
            const purchaseType = product.purchase_type || 'packing';
            const costPerPack = parseFloat(product.cost_per_pack) || 0;
            const costPerUnit = parseFloat(product.cost_per_unit) || (costPerPack / itemsPerPack) || parseFloat(product.cost_price) || 0;
            const sellPerPack = parseFloat(product.sell_per_pack) || 0;

            const displayPrice = purchaseType === 'packing'
                ? (costPerPack > 0 ? costPerPack.toFixed(2) : '')
                : (costPerUnit > 0 ? costPerUnit.toFixed(2) : '');

            const newItem = {
                product_name: product.product_name || '',
                batch_no: product.batch_no || '',
                product_code: product.product_code || '',
                category: product.category || '',
                company: product.company || '',
                color: product.color || '',
                size: product.size || '',
                expiry_date: product.expiry_date || '',
                purchase_type: purchaseType,
                packing_type: packingType,
                sub_packing_type: subPackingType,
                items_per_pack: itemsPerPack,
                unit_of_measure: unitOfMeasure,
                price: displayPrice,
                cost_per_pack: costPerPack > 0 ? costPerPack.toFixed(2) : '',
                sell_per_pack: sellPerPack > 0 ? sellPerPack.toFixed(2) : '',
                quantity: '1',
                product_id: product.id || '',
                _pack_price: costPerPack > 0 ? costPerPack.toFixed(2) : '',
                _unit_price: costPerUnit > 0 ? costPerUnit.toFixed(2) : '',
                available_stock_packs: product.quantity || 0,
                bquantity: product.bquantity || ''
            };

            setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
            const typeLabel = purchaseType === 'sub_packing' ? 'Sub Packing (Units)' : 'Packing (Packs)';
            const priceLabel = purchaseType === 'sub_packing'
                ? `Unit price: Rs. ${displayPrice}`
                : `Pack price: Rs. ${displayPrice}`;
            showToast('success', `${product.product_name} added — ${typeLabel} | ${priceLabel}`);
        }
        setGlobalProductSearchTerm('');
        setShowGlobalProductDropdown(false);
    };

    const handleSupplierSearch = async (term) => {
        setSearchTerm(term);
        setShowSupplierDropdown(true);
        if (!user?.username || !currentShop?.shop_id) return;
        try {
            const r = await fetch(SUPPLIER_API, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_suppliers', searchTerm: term, username: user.username, shop_id: currentShop.shop_id })
            });
            const d = await r.json();
            if (d.status === 'success' && d.data) setFilteredSuppliers(d.data);
        } catch (e) { console.error('Supplier search error:', e); }
    };

    const selectSupplier = (supplier) => {
        setFormData(prev => ({
            ...prev, supplierid: supplier.supplierid,
            supplier_name: supplier.supplier_name || '',
            phone: supplier.phone || '', address: supplier.address || ''
        }));
        setSearchTerm(supplier.supplier_name || '');
        setShowSupplierDropdown(false);
        if (errors.supplier) setErrors(prev => ({ ...prev, supplier: '' }));
        showToast('success', `Supplier "${supplier.supplier_name}" selected`);
    };

    const handleItemChange = (index, field, value) => {
        if (field === 'batch_update') {
            setFormData(prev => {
                const items = [...prev.items];
                items[index] = { ...items[index], ...value };
                return { ...prev, items };
            });
        } else {
            setFormData(prev => {
                const items = [...prev.items];
                const cur = items[index];
                const updated = { ...cur, [field]: value };
                if (field === 'price') {
                    if ((cur.purchase_type || 'packing') === 'packing') {
                        updated._pack_price = value;
                    } else {
                        updated._unit_price = value;
                    }
                }
                items[index] = updated;
                return { ...prev, items };
            });
        }
        const errKey = `${field}_${index}`;
        if (errors[errKey]) setErrors(prev => { const e = { ...prev }; delete e[errKey]; return e; });
    };

    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                product_name: '', batch_no: '', product_code: '', product_id: '',
                category: '', company: '', color: '', size: '', expiry_date: '',
                price: '', quantity: '1',
                purchase_type: 'packing', packing_type: '', sub_packing_type: '',
                items_per_pack: '', unit_of_measure: '', cost_per_pack: '', sell_per_pack: '',
                _pack_price: '', _unit_price: ''
            }]
        }));
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const items = [...formData.items];
            items.splice(index, 1);
            setFormData(prev => ({ ...prev, items }));
        } else {
            setFormData(prev => ({ ...prev, items: [] }));
        }
    };

    const calculateTotals = () => {
        let grandTotal = 0;
        formData.items.forEach(item => {
            const qty = parseFloat(item.quantity) || 0;
            if ((item.purchase_type || 'packing') === 'sub_packing') {
                grandTotal += (parseFloat(item.price) || 0) * qty;
            } else {
                grandTotal += (parseFloat(item.cost_per_pack) || parseFloat(item.price) || 0) * qty;
            }
        });
        const paid = parseFloat(formData.paid) || 0;
        setFormData(prev => ({
            ...prev,
            grand_total: grandTotal.toFixed(2),
            balance: (grandTotal - paid).toFixed(2)
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { showToast('error', 'File size must be less than 5MB'); return; }
        const reader = new FileReader();
        reader.onload = (ev) => {
            setFormData(prev => ({ ...prev, bill_file: { name: file.name, type: file.type, data: ev.target.result.split(',')[1] } }));
        };
        reader.readAsDataURL(file);
    };

    const validateForm = () => {
        const errs = {};
        if (!formData.bill_no?.trim()) errs.bill_no = 'Bill number is required';
        if (!formData.supplierid) errs.supplier = 'Supplier is required';
        if (!formData.date) errs.date = 'Date is required';
        if (!formData.items || formData.items.length === 0) {
            errs.items = 'At least one item is required';
        } else {
            formData.items.forEach((item, i) => {
                if (!item.product_name?.trim()) errs[`product_name_${i}`] = 'Product name required';
                if (!item.batch_no?.trim()) errs[`batch_no_${i}`] = 'Batch number required';
                if (!item.price || parseFloat(item.price) <= 0) errs[`price_${i}`] = 'Price required';
                if (!item.quantity || parseFloat(item.quantity) <= 0) errs[`quantity_${i}`] = 'Quantity required';
                if ((item.purchase_type || 'packing') === 'packing') {
                    if (!item.cost_per_pack || parseFloat(item.cost_per_pack) <= 0) {
                        errs[`cost_per_pack_${i}`] = 'Cost per pack required';
                    }
                }
            });
        }
        const paid = parseFloat(formData.paid);
        if (isNaN(paid) || paid < 0) errs.paid = 'Please enter a valid paid amount';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
        if (name === 'paid') {
            const paidVal = parseFloat(value) || 0;
            const gt = parseFloat(formData.grand_total) || 0;
            setFormData(prev => ({ ...prev, balance: (gt - paidVal).toFixed(2) }));
        }
    };

    const generateNextBillNumber = () => {
        const d = new Date();
        const y = d.getFullYear().toString().substr(-2);
        const m = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const r = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `PUR-${y}${m}${day}-${r}`;
    };

    const handleGenerateBillNumber = () => {
        if (!formData.bill_no) setFormData(prev => ({ ...prev, bill_no: generateNextBillNumber() }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSavingRef.current) { showToast('warning', 'Please wait...'); return; }
        if (!currentShop?.shop_id) { showToast('error', 'No shop selected.'); return; }
        if (!validateForm()) return;

        isSavingRef.current = true;
        setSaving(true);

        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) throw new Error('User not authenticated');

            const processedItems = formData.items.map(item => {
    const isPacking = (item.purchase_type || 'packing') === 'packing';
    const quantityEntered = parseFloat(item.quantity) || 0;
    const itemsPerPack = parseFloat(item.items_per_pack) || 1;

    if (isPacking) {
        const costPerPack = parseFloat(item.cost_per_pack) || (parseFloat(item.price) || 0);
        const pricePerUnit = itemsPerPack > 0 ? costPerPack / itemsPerPack : costPerPack;
        const totalLine = costPerPack * quantityEntered;

        return {
            product_name: item.product_name || '',
            batch_no: item.batch_no || '',
            product_code: item.product_code || '',
            category: item.category || '',
            company: item.company || '',
            color: item.color || '',
            size: item.size || '',
            expiry_date: item.expiry_date || null,  // FIX: Send null for empty
            purchase_type: 'packing',
            is_sub_packing_purchase: false,
            packing_type: item.packing_type || null,
            sub_packing_type: item.sub_packing_type || null,
            items_per_pack: itemsPerPack,
            unit_of_measure: item.unit_of_measure || 'piece',
            price: pricePerUnit,
            cost_per_pack: costPerPack,
            sell_per_pack: parseFloat(item.sell_per_pack) || null,
            quantity: quantityEntered,
            total: totalLine.toFixed(2),
            product_id: item.product_id || null
        };
    } else {
        const pricePerUnit = parseFloat(item.price) || 0;
        const totalLine = pricePerUnit * quantityEntered;

        return {
            product_name: item.product_name || '',
            batch_no: item.batch_no || '',
            product_code: item.product_code || '',
            category: item.category || '',
            company: item.company || '',
            color: item.color || '',
            size: item.size || '',
            expiry_date: item.expiry_date || null,  // FIX: Send null for empty
            purchase_type: 'sub_packing',
            is_sub_packing_purchase: true,
            packing_type: null,
            sub_packing_type: null,
            items_per_pack: itemsPerPack,
            unit_of_measure: item.unit_of_measure || 'piece',
            price: pricePerUnit,
            cost_per_pack: (pricePerUnit * itemsPerPack).toFixed(2),
            sell_per_pack: null,
            quantity: quantityEntered,
            total: totalLine.toFixed(2),
            product_id: item.product_id || null
        };
    }
});

            const payload = {
                action: isEditMode ? 'edit' : 'add',
                username: currentUsername,
                shop_id: parseInt(currentShop.shop_id),
                date: formData.date,
                bill_no: formData.bill_no,
                supplierid: parseInt(formData.supplierid) || 0,
                supplier_name: formData.supplier_name,
                phone: formData.phone || '',
                address: formData.address || '',
                grand_total: parseFloat(formData.grand_total || 0).toFixed(2),
                paid: parseFloat(formData.paid || 0).toFixed(2),
                items: processedItems
            };

            if (formData.bill_file) payload.bill_file = JSON.stringify(formData.bill_file);
            if (isEditMode && purchaseId) payload.id = purchaseId;

            const response = await fetch(PURCHASE_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', isEditMode ? 'Purchase updated!' : 'Purchase saved!');

                if (!isEditMode) {
                    setFormData({ ...initialFormData, date: new Date().toISOString().split('T')[0], items: [] });
                    setSearchTerm('');
                    setErrors({});
                    setGlobalProductSearchTerm('');
                    setTimeout(() => { if (billNumberRef.current) billNumberRef.current.focus(); }, 100);
                }

                if (fileInputRef.current) fileInputRef.current.value = '';
                if (onSuccess) onSuccess(data.purchaseid || purchaseId);
            } else {
                showToast('error', data.message || 'Failed to save purchase');
            }
        } catch (error) {
            console.error('Purchase submission error:', error);
            showToast('error', error.message || 'Network error occurred');
        } finally {
            isSavingRef.current = false;
            setSaving(false);
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{isEditMode ? (translations?.editPurchase || 'Edit Purchase') : (translations?.addPurchase || 'New Purchase')}</h1>
                            <p>{translations?.purchaseDetails || 'Purchase Details'}</p>
                        </div>
                    </div>
                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button className="ozui-action-btn clear-draft" onClick={clearDraft} disabled={saving} title="Clear Form">
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>{translations?.clear || 'Clear'}</span>
                            </button>
                            <button className="ozui-action-btn save-draft" onClick={saveDraft} disabled={saving} title="Save Draft">
                                <FontAwesomeIcon icon={faCopy} />
                                <span>{translations?.saveDraft || 'Save Draft'}</span>
                            </button>
                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button className="ozui-action-btn drafts-btn" onClick={() => setShowDraftsList(!showDraftsList)} title="View Drafts">
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations?.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations?.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations?.noDraftsSaved || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">{draft.bill_no || `Purchase #${draft.id}`}</div>
                                                            <div className="draft-details">
                                                                {draft.supplier_name && <span>{draft.supplier_name}</span>}
                                                                {draft.items && <span>{draft.items.length} items</span>}
                                                            </div>
                                                            <div className="draft-date">{new Date(draft.savedAt).toLocaleDateString()}</div>
                                                        </div>
                                                        <button className="delete-draft-btn" onClick={e => { e.stopPropagation(); deleteDraft(draft.id); }}>
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                {/* Barcode Scanner Status Bar */}
                <div style={{
                    background: isScannerActive ? '#f0fdf4' : '#f9fafb',
                    border: `1px solid ${isScannerActive ? '#bbf7d0' : '#e5e7eb'}`,
                    borderRadius: '6px',
                    padding: '6px 12px',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '0.85rem'
                }}>
                    <FontAwesomeIcon icon={faBarcode} style={{ color: isScannerActive ? '#10b981' : '#9ca3af' }} />
                    <span style={{ flex: 1, color: isScannerActive ? '#059669' : '#6b7280', fontWeight: 500 }}>
                        {isScannerActive ? (translations?.scannerReady || 'Scanner Ready') : 'Scanner Paused'}
                    </span>
                    <button
                        type="button"
                        onClick={() => setIsScannerActive(!isScannerActive)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: isScannerActive ? '#059669' : '#6b7280',
                            fontSize: '1.1rem',
                            padding: '2px 6px'
                        }}
                    >
                        <FontAwesomeIcon icon={isScannerActive ? faToggleOn : faToggleOff} />
                    </button>
                </div>

                {/* Hidden Barcode Input */}
                <input
                    ref={barcodeInputRef}
                    type="text"
                    style={{ position: 'absolute', left: '-9999px', opacity: 0, width: '1px', height: '1px' }}
                    autoComplete="off"
                    tabIndex={-1}
                    onChange={() => {}}
                />

                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input type="date" name="date" id="date" value={formData.date} onChange={handleChange} placeholder=" " disabled={saving} />
                            <label htmlFor="date"><FontAwesomeIcon icon={faCalendarAlt} />{translations?.date || 'Date'} <span>*</span></label>
                        </div>
                        {errors.date && <small>{errors.date}</small>}
                    </div>

                    <div className={`ozui-field ${errors.bill_no ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper ozui-bill-input-wrapper">
                            <input ref={billNumberRef} type="text" name="bill_no" id="bill_no" value={formData.bill_no} onChange={handleChange} placeholder=" " disabled={saving} />
                            <label htmlFor="bill_no"><FontAwesomeIcon icon={faFileInvoice} />{translations?.billNumber || 'Bill Number'} <span>*</span></label>
                            {!formData.bill_no && !saving && (
                                <button type="button" className="ozui-input-generate-btn" onClick={handleGenerateBillNumber} title="Generate">
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            )}
                        </div>
                        {errors.bill_no && <small>{errors.bill_no}</small>}
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.supplier ? 'ozui-error' : ''}`} ref={supplierSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input type="text" id="supplier_search" value={searchTerm} onChange={e => handleSupplierSearch(e.target.value)} onFocus={() => setShowSupplierDropdown(true)} placeholder=" " disabled={saving} autoComplete="off" />
                            <label htmlFor="supplier_search"><FontAwesomeIcon icon={faUser} />{translations?.supplier || 'Supplier'} <span>*</span></label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {showSupplierDropdown && (
                            <div className="ozui-supplier-dropdown">
                                {filteredSuppliers && filteredSuppliers.length > 0 ? (
                                    filteredSuppliers.map(s => (
                                        <div key={s.supplierid} className="ozui-dropdown-item" onClick={() => selectSupplier(s)}>
                                            <div className="ozui-supplier-name">{s.supplier_name}</div>
                                            {s.phone && <div className="ozui-supplier-phone"><FontAwesomeIcon icon={faPhone} /> {s.phone}</div>}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-dropdown-empty">{translations?.noSuppliersFound || 'No suppliers found'}</div>
                                )}
                            </div>
                        )}
                        {errors.supplier && <small>{errors.supplier}</small>}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input type="text" name="supplier_name" id="supplier_name" value={formData.supplier_name} readOnly placeholder=" " className="ozui-readonly-input" />
                            <label htmlFor="supplier_name"><FontAwesomeIcon icon={faStore} />{translations?.supplierName || 'Supplier Name'}</label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input type="tel" name="phone" id="phone" value={formData.phone} onChange={handleChange} placeholder=" " disabled={saving} />
                            <label htmlFor="phone"><FontAwesomeIcon icon={faPhone} />{translations?.phone || 'Phone'} <span>({translations?.optional || 'Optional'})</span></label>
                        </div>
                    </div>
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input type="text" name="address" id="address" value={formData.address} onChange={handleChange} placeholder=" " disabled={saving} />
                            <label htmlFor="address"><FontAwesomeIcon icon={faMapMarkerAlt} />{translations?.address || 'Address'} <span>({translations?.optional || 'Optional'})</span></label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper ozui-file-wrapper">
                            <input type="file" id="bill_file" ref={fileInputRef} onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" disabled={saving} className="ozui-file-input" />
                            <label htmlFor="bill_file">
                                <FontAwesomeIcon icon={faUpload} />{translations?.billFile || 'Bill File'}
                                <span>({translations?.optional || 'Optional'})</span>
                            </label>
                            <span className="ozui-file-name">{formData.bill_file ? formData.bill_file.name : (translations?.noFile || 'No file chosen')}</span>
                        </div>
                    </div>
                </div>

                {/* Global product search */}
                <div className="ozui-form-row">
                    <div className="ozui-field" ref={productSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                id="global_product_search"
                                value={globalProductSearchTerm}
                                onChange={e => { setGlobalProductSearchTerm(e.target.value); searchGlobalProducts(e.target.value); }}
                                onFocus={() => globalProductSearchTerm.length >= 2 && setShowGlobalProductDropdown(true)}
                                placeholder=" "
                                disabled={saving}
                                autoComplete="off"
                            />
                            <label htmlFor="global_product_search">
                                <FontAwesomeIcon icon={faSearch} />{translations?.searchProducts || 'Search Products'}
                            </label>
                            {searchingGlobalProducts && <FontAwesomeIcon icon={faSpinner} spin className="ozui-search-indicator" />}
                        </div>
                        {showGlobalProductDropdown && filteredGlobalProducts.length > 0 && (
                            <div className="ozui-product-dropdown">
                                {filteredGlobalProducts.map(product => (
                                    <div key={product.id} className="ozui-product-item" onClick={() => addProductToItems(product)}>
                                        <div className="ozui-product-item-name">
                                            {product.product_name}
                                            {product.product_code && <span className="ozui-product-code-badge">{product.product_code}</span>}
                                            {product.color && <span className="ozui-product-color-badge">{product.color}</span>}
                                        </div>
                                        <div className="ozui-product-item-details">
                                            {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>}
                                            {product.category && <span><FontAwesomeIcon icon={faBox} /> {product.category}</span>}
                                            {product.company && <span><FontAwesomeIcon icon={faBuilding} /> {product.company}</span>}
                                            {product.size && <span><FontAwesomeIcon icon={faArrowsAltH} /> {product.size}</span>}
                                        </div>
                                        <div className="ozui-product-item-price-stock">
                                            <span className="ozui-product-price">
                                                <FontAwesomeIcon icon={faRupeeSign} />
                                                {' '}
                                                {(product.purchase_type === 'sub_packing'
                                                    ? parseFloat(product.cost_per_unit || product.cost_price || 0)
                                                    : parseFloat(product.cost_per_pack || product.cost_price || 0)
                                                ).toFixed(2)}
                                                {product.purchase_type === 'sub_packing' ? ' /unit' : ' /pack'}
                                            </span>
                                            <span className={`ozui-product-stock ${parseFloat(product.quantity) <= (product.quantity_alert || 5) ? 'ozui-low-stock' : ''}`}>
                                                Stock: {product.quantity} packs / {parseBQuantityNumeric(product.bquantity || '0').toFixed(2)} units
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <ItemsTable
                    items={formData.items}
                    onItemChange={handleItemChange}
                    onRemove={removeItem}
                    onAdd={addItem}
                    errors={errors}
                    disabled={saving}
                    translations={translations}
                    isRTL={isRTL}
                    user={user}
                    shopId={currentShop?.shop_id}
                    showToast={showToast}
                />

                {formData.items.length > 0 && (
                    <div className="ozui-form-section">
                        <div className="ozui-calculations">
                            <div className="ozui-calculation-row ozui-grand-total">
                                <span className="ozui-calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.grand_total}</span>
                            </div>
                            <div className={`ozui-calculation-row ${errors.paid ? 'ozui-error' : ''}`}>
                                <span className="ozui-calculation-label">{translations?.paid || 'Paid Amount'}:</span>
                                <div className="ozui-paid-input-wrapper">
                                    <input type="number" name="paid" value={formData.paid} onChange={handleChange} step="0.01" min="0" max={formData.grand_total} placeholder="0.00" disabled={saving} />
                                </div>
                                {errors.paid && <small>{errors.paid}</small>}
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.balance || 'Balance'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.balance}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="ozui-form-actions">
                    <button type="submit" className="ozui-btn ozui-btn-primary" disabled={saving}>
                        {saving ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /><span>{translations?.saving || 'Saving...'}</span></>
                        ) : (
                            <><FontAwesomeIcon icon={faSave} /><span>{isEditMode ? (translations?.updatePurchase || 'Update Purchase') : (translations?.savePurchase || 'Save Purchase')}</span></>
                        )}
                    </button>
                </div>
            </form>

            {loading && (
                <div className="ozui-loading-overlay">
                    <div className="ozui-loading-content">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PharmacyPurchases;